import { useState } from "react";
import { BRAND, BRAND_LIGHT, BRAND_DARK, ARCH_COLORS, GREEN, MUTED, text, WHITE } from "./constants";
import Logo from "./Logo";

// ─── CARGADOR CON EL LOGO (23/10) ─────────────────────────────────────────────
// FUENTE ÚNICA de la espera larga en rutment.com (gemelo: LogoLoader en
// Layout.jsx del portal). El logo aparece en azul claro y se pinta de
// izquierda a derecha, en bucle. Es para esperas de página o de bloque grande
// (entrar, cargar posiciones, formularios públicos, preparar la guía); dentro
// de un botón o de un paso pequeño sigue el spinner de siempre.
//
// `done` (solo la guía de entrevista, decisión de Borja 23/10): el logo queda
// entero, el cursor da un salto y suelta confeti UNA vez, y todo se apaga en
// LOGO_DONE_MS. El confeti no va en ningún otro sitio.
// `tone="dark"`: en blanco, para fondos oscuros (portada).
// Solo anima transform, opacity y el recorte del logo; con "reducir
// movimiento", logo quieto y sin confeti.
export const LOGO_DONE_MS = 1300;
// Bucle (24/10): se pinta en la mitad de la vuelta, se queda entero, se apaga
// despacio y descansa un instante en azul claro antes de repetir. Antes se
// apagaba en menos de medio segundo y volvía a empezar sin pausa: parpadeaba.
// Cada vuelta empieza y acaba invisible: Safari (iPhone) pinta a veces un
// fotograma con el logo entero al cambiar de vuelta, y así no se ve.
const LOADER_CSS = `
@keyframes rtLogoSweep { 0% { clip-path: inset(-12px 100% -12px 0); opacity: 0 } 4% { opacity: 1 } 50%, 66% { clip-path: inset(-12px -12px -12px 0); opacity: 1 } 90%, 100% { clip-path: inset(-12px -12px -12px 0); opacity: 0 } }
@keyframes rtLogoDone { 0%, 45% { opacity: 1 } 100% { opacity: 0 } }
@keyframes rtLogoPop { 0% { transform: scale(1) } 18% { transform: scale(1.35) } 40%, 100% { transform: scale(1) } }
@keyframes rtLogoConf {
  0%   { opacity: 1; transform: translate(0, 0) rotate(0) scale(var(--s)); animation-timing-function: cubic-bezier(.1,.8,.3,1) }
  25%  { opacity: 1; transform: translate(var(--x), var(--y)) rotate3d(1, .6, .3, var(--r)) scale(var(--s)); animation-timing-function: ease-out }
  100% { opacity: 0; transform: translate(calc(var(--x) * 1.12), calc(var(--y) * 1.12)) rotate3d(.6, 1, .4, calc(var(--r) * 1.5)) scale(calc(var(--s) * .35)) }
}
@keyframes rtLogoSpark { 0% { opacity: 0; transform: translate(var(--x), var(--y)) scale(0) } 15% { opacity: 1; transform: translate(var(--x), var(--y)) scale(1.3) } 45%, 100% { opacity: 0; transform: translate(var(--x), var(--y)) scale(0) } }
@keyframes rtLogoAppear { from { opacity: 0 } to { opacity: 1 } }
.rt-logo-fill { position: absolute; inset: 0; animation: rtLogoSweep 2.8s ease-in-out infinite }
.rt-logo-fill.done { animation: rtLogoDone ${LOGO_DONE_MS}ms ease-in forwards }
.rt-logo-fill.done .rt-logo-cursor { animation: rtLogoPop .7s ease-out }
.rt-logo-burst { position: absolute; top: 50%; width: 0; height: 0; pointer-events: none; perspective: 300px }
.rt-logo-burst i { position: absolute; left: 0; top: 0; opacity: 0; animation: rtLogoConf ${LOGO_DONE_MS}ms linear forwards }
.rt-logo-burst b { position: absolute; width: 6px; height: 6px; margin: -3px 0 0 -3px; opacity: 0; clip-path: polygon(50% 0, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0 50%, 40% 40%); animation: rtLogoSpark ${LOGO_DONE_MS}ms ease-out forwards }
.rt-logo-appear { opacity: 0; animation: rtLogoAppear .3s ease-out forwards }
@media (prefers-reduced-motion: reduce) { .rt-logo-fill, .rt-logo-fill.done { animation: none } .rt-logo-burst { display: none } }
`;

// Confeti: 90 piezas de seis formas y tamaños distintos, en abanico hacia
// arriba desde el cursor, y 5 destellos alrededor. Se sortea una vez.
const CONF_COLORS = [BRAND, GREEN, BRAND_LIGHT, ARCH_COLORS[1], BRAND, GREEN, BRAND_DARK];
const CONF_SHAPES = {
  strip: { width: 2, height: 5, borderRadius: 1 },
  square: { width: 3, height: 3, borderRadius: 0.5 },
  dot: { width: 3, height: 3, borderRadius: "50%" },
  dust: { width: 1.5, height: 1.5, borderRadius: "50%" },
  thin: { width: 6, height: 1.5, borderRadius: 1 },
  star: { width: 5, height: 5, clipPath: "polygon(50% 0, 62% 38%, 100% 50%, 62% 62%, 50% 100%, 38% 62%, 0 50%, 38% 38%)" },
};
const CONF_MIX = ["strip", "strip", "square", "dot", "dot", "dust", "dust", "thin", "star"];
const makeConfetti = () => {
  const rnd = (a, b) => a + Math.random() * (b - a);
  const pieces = Array.from({ length: 90 }, (_, i) => {
    const ang = rnd(-178, -2) * Math.PI / 180;
    const dist = rnd(18, 78);
    const shape = CONF_SHAPES[CONF_MIX[Math.floor(Math.random() * CONF_MIX.length)]];
    return {
      ...shape, marginLeft: -shape.width / 2, marginTop: -shape.height / 2, background: CONF_COLORS[i % CONF_COLORS.length],
      "--x": `${Math.round(Math.cos(ang) * dist * 1.25)}px`, "--y": `${Math.round(Math.sin(ang) * dist * 0.72)}px`,
      "--r": `${Math.round(rnd(-360, 360))}deg`, "--s": rnd(0.5, 1.1).toFixed(2), animationDelay: `${rnd(0, 0.1).toFixed(2)}s`,
    };
  });
  const sparks = Array.from({ length: 5 }, (_, i) => {
    const ang = rnd(0, 360) * Math.PI / 180;
    return { background: i % 2 ? GREEN : BRAND, "--x": `${Math.round(Math.cos(ang) * rnd(14, 22))}px`, "--y": `${Math.round(Math.sin(ang) * rnd(14, 22))}px`, animationDelay: `${(i * 0.04).toFixed(2)}s` };
  });
  return { pieces, sparks };
};

// Solo el logo animado
export const LogoLoader = ({ size = 28, done = false, tone = "light" }) => {
  const [confetti] = useState(() => (done ? makeConfetti() : null));
  // El barrido va con el reloj, no con el momento en que se monta: si una
  // espera sigue a otra (sesión → posiciones), el logo continúa por donde iba
  // en vez de volver a empezar (23/10)
  const [phase] = useState(() => `${-(Date.now() % 2800)}ms`);
  const dark = tone === "dark";
  // Mitad del bloque del cursor de Logo.jsx (0.46 × tamaño): de ahí sale el confeti
  const cursorHalf = Math.round(size * 0.46) / 2;
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <style>{LOADER_CSS}</style>
      <Logo size={size} color={dark ? "rgba(255,255,255,0.14)" : BRAND_LIGHT} />
      <span className={`rt-logo-fill${done ? " done" : ""}`} style={done ? undefined : { animationDelay: phase }}><Logo size={size} color={dark ? WHITE : BRAND} cursorClassName="rt-logo-cursor" /></span>
      {done && confetti && (
        <span className="rt-logo-burst" style={{ right: cursorHalf }}>
          {confetti.pieces.map((st, i) => <i key={i} style={st} />)}
          {confetti.sparks.map((st, i) => <b key={`s${i}`} style={st} />)}
        </span>
      )}
    </span>
  );
};

// Espera de página o de bloque: el logo centrado y, si se da, una línea debajo.
// Aparece tras un instante (`appearDelay`) para que una carga rápida no
// enseñe un destello del cargador.
// `fixed` (esperas de página entera): centrado en la PANTALLA, no en su caja.
// Así todas las fases de una misma carga (sesión → datos → página) ponen el
// logo en el mismo sitio; antes saltaba de altura y mareaba (23/10). Las
// que siguen a otra van con `appearDelay={null}`: sin fundido, ya está a la vista.
export const PageLoader = ({ label, tone = "light", minHeight = "60vh", background, appearDelay = 0.25, fixed = false }) => (
  <div role="status" aria-live="polite" aria-label={label || "Loading"}
    style={{ ...(fixed ? { position: "fixed", inset: 0, zIndex: 50, pointerEvents: "none" } : { minHeight }), display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, background, textAlign: "center", padding: 16, boxSizing: "border-box" }}>
    <div className={appearDelay == null ? undefined : "rt-logo-appear"} style={{ ...(appearDelay == null ? null : { animationDelay: `${appearDelay}s` }), display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
      <LogoLoader tone={tone} />
      {label && <div style={{ ...text.bodySm, color: tone === "dark" ? "rgba(255,255,255,0.6)" : MUTED }}>{label}</div>}
    </div>
  </div>
);
